import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import DemoHeroCard from './components/DemoHeroCard';
import DemoRiasecCard from './components/DemoRiasecCard';
import DemoArchetypeSpotlightCard from './components/DemoArchetypeSpotlightCard';
import DemoBehaviorCompassCard from './components/DemoBehaviorCompassCard';
import DemoCareerConstellationCard from './components/DemoCareerConstellationCard';
import DemoResultsPrintPage from './DemoResultsPrintPage';
import selectDemoPreset from './selectDemoPreset';

const DemoResultsPage = ({ variantKey }) => {
    const preset = selectDemoPreset(variantKey);
    const [isPreparingPrint, setIsPreparingPrint] = useState(false);

    useEffect(() => {
        const handleAfterPrint = () => {
            setIsPreparingPrint(false);
        };

        window.addEventListener('afterprint', handleAfterPrint);

        return () => {
            window.removeEventListener('afterprint', handleAfterPrint);
        };
    }, []);

    const handlePrint = () => {
        if (isPreparingPrint) {
            return;
        }

        setIsPreparingPrint(true);

        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                window.print();
            });
        });
    };

    const printStyles = useMemo(() => `
        @page {
            size: A4 landscape;
            margin: 12mm;
        }

        @media screen {
            .demo-dedicated-print-portal {
                display: none !important;
            }
        }

        @media print {
            * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
            }

            html,
            body {
                background: #ffffff !important;
                margin: 0 !important;
                padding: 0 !important;
                width: auto !important;
                min-width: 0 !important;
            }

            body > *:not(.demo-dedicated-print-portal) {
                display: none !important;
            }

            .demo-dedicated-print-portal {
                display: block !important;
                visibility: visible !important;
                position: static !important;
                width: 100% !important;
                padding: 0 !important;
                background: #ffffff !important;
            }

            .demo-dedicated-print-portal,
            .demo-dedicated-print-portal * {
                visibility: visible !important;
            }

            .demo-print-toolbar {
                display: none !important;
            }

            .demo-dedicated-print-surface {
                width: 269mm !important;
                margin: 0 auto !important;
            }

            .demo-dedicated-print-page {
                break-after: page !important;
                page-break-after: always !important;
                overflow: visible !important;
                box-shadow: none !important;
            }

            .demo-dedicated-print-page:last-child {
                break-after: auto !important;
                page-break-after: auto !important;
            }

            .demo-dedicated-print-section,
            .demo-dedicated-print-grid,
            .demo-dedicated-print-card {
                break-inside: avoid !important;
                page-break-inside: avoid !important;
            }
        }
    `, []);

    const printSurface = typeof document !== 'undefined' && document.body
        ? createPortal(<DemoResultsPrintPage preset={preset} />, document.body)
        : null;

    return (
        <>
            <style>{printStyles}</style>

            <div className="demo-results-live-page min-h-[80vh] px-4 py-8 md:py-10 animate-fadeIn">
                <div className="demo-print-toolbar max-w-7xl mx-auto mb-5 md:mb-6 flex justify-end">
                    <button
                        type="button"
                        onClick={handlePrint}
                        disabled={isPreparingPrint}
                        className="inline-flex items-center gap-2 rounded-full bg-[var(--color-brand)] px-5 py-3 text-sm md:text-base font-bold text-white shadow-[0_12px_30px_rgba(146,39,143,0.25)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                        <span aria-hidden="true">↓</span>
                        <span>{isPreparingPrint ? 'Opening print preview…' : 'Download / Print Demo Report'}</span>
                    </button>
                </div>

                <div className="demo-results-live-shell max-w-7xl mx-auto space-y-6 md:space-y-8">
                    <div className="demo-results-live-section">
                        <DemoHeroCard hero={preset.hero} />
                    </div>

                    <div className="demo-results-live-section grid grid-cols-1 xl:grid-cols-[1.1fr_0.9fr] gap-6 md:gap-8 items-stretch">
                        <div className="demo-results-live-card min-w-0">
                            <DemoRiasecCard riasec={preset.riasec} />
                        </div>

                        <div className="demo-results-live-card min-w-0">
                            <DemoArchetypeSpotlightCard archetype={preset.archetype} />
                        </div>
                    </div>

                    <div className="demo-results-live-section demo-results-live-card">
                        <DemoBehaviorCompassCard compass={preset.compass} />
                    </div>

                    <div className="demo-results-live-section demo-results-live-card">
                        <DemoCareerConstellationCard careerMatrix={preset.careerMatrix} />
                    </div>

                    <div className="text-center pt-2">
                        <p className="text-xs md:text-sm uppercase tracking-[0.24em] text-gray-400 brand-text">
                            Demo preview — full assessment report will be handled separately
                        </p>
                    </div>
                </div>
            </div>

            {printSurface}
        </>
    );
};

export default DemoResultsPage;
